import { useMemo, useState } from 'react';
import { Check, Search, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { DEPT_LABELS } from '../constants';
import type { CreateUserPayload } from '../types';
import {
  defaultWorkspaceAccessForRole,
  WORKSPACE_ACCESS_CATALOG,
  type WorkspaceAccessGroup,
  type WorkspaceAccessKey,
} from '@/shared/permissions/workspaceAccess';

// Readable section headers so a specific module (e.g. Dispatch, under
// "Operations") is easy to scan for in what is otherwise a flat 28-item list
// inside a small scrolling box — that's how a real module ends up looking
// "missing" when it was only ever a few rows further down.
const MODULE_GROUP_LABELS: Record<WorkspaceAccessGroup, string> = {
  crm: 'Sales & CRM',
  finance: 'Finance',
  operations: 'Operations',
  services: 'Services & Projects',
  work: 'Work Management',
  hr: 'HR',
  system: 'System',
};
import {
  buildCreateUserPayload,
  CREATE_USER_DEPARTMENTS,
  initialCreateUserForm,
  rolesForDepartment,
  type CreateUserFormState,
  type CreateUserRole,
} from './createUserForm';
import { useMasterDepartments, useMasterDesignations } from '@/features/recruitment/api';
import { useShifts } from '@/features/attendance/hooks/useBiometricAdmin';

interface CreateUserModalProps {
  open: boolean;
  /** Default department (the admin's own dept). Superadmin makes an explicit selection. */
  department: string;
  /** Superadmin can choose role + department; admin is locked to employee + own dept. */
  isSuperAdmin: boolean;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateUserPayload) => void;
}

const CREATE_DEPTS = CREATE_USER_DEPARTMENTS;

export function CreateUserModal({ open, department, isSuperAdmin, loading, onClose, onSubmit }: CreateUserModalProps) {
  const empty = initialCreateUserForm(department, { requireDepartmentSelection: isSuperAdmin });
  const [form, setForm] = useState<CreateUserFormState>(empty);
  const [moduleSearch, setModuleSearch] = useState('');

  // Fetch master data for dropdowns
  const { data: masterDepartments = [] } = useMasterDepartments();
  const { data: masterDesignations = [] } = useMasterDesignations();
  const { data: masterShifts = [] } = useShifts();

  // Reset the form whenever the modal opens or the requester's department changes.
  const [prevOpen, setPrevOpen] = useState(open);
  const [prevDepartment, setPrevDepartment] = useState(department);
  const [selectedDesignationValue, setSelectedDesignationValue] = useState('');
  if (open !== prevOpen || department !== prevDepartment) {
    setPrevOpen(open);
    setPrevDepartment(department);
    if (open) { setForm(empty); setSelectedDesignationValue(''); }
  }

  const set = <K extends keyof CreateUserFormState>(key: K, value: CreateUserFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const setDepartment = (nextDepartment: string) => {
    setSelectedDesignationValue('');
    setForm((prev) => ({
      ...prev,
      department: nextDepartment,
      role: 'employee',
      designation: '',
      workspaceAccess: defaultWorkspaceAccessForRole(nextDepartment, 'employee'),
    }));
  };

  // Department + role only seed which boxes start checked — a suggestion, not
  // a floor. The superadmin has full authority to uncheck any of them or add
  // any module the suggestion missed; nothing here is locked.
  const suggestedWorkspaceAccess = form.department
    ? defaultWorkspaceAccessForRole(form.department, form.role)
    : [];

  const groupedModules = useMemo(() => {
    const term = moduleSearch.trim().toLowerCase();
    const matched = term
      ? WORKSPACE_ACCESS_CATALOG.filter(
          (m) => m.label.toLowerCase().includes(term) || m.description.toLowerCase().includes(term),
        )
      : WORKSPACE_ACCESS_CATALOG;
    const groups = new Map<WorkspaceAccessGroup, typeof WORKSPACE_ACCESS_CATALOG[number][]>();
    for (const module of matched) {
      const list = groups.get(module.group) ?? [];
      list.push(module);
      groups.set(module.group, list);
    }
    return [...groups.entries()];
  }, [moduleSearch]);

  const toggleWorkspaceAccess = (key: WorkspaceAccessKey) => {
    setForm((prev) => ({
      ...prev,
      workspaceAccess: prev.workspaceAccess.includes(key)
        ? prev.workspaceAccess.filter((item) => item !== key)
        : [...prev.workspaceAccess, key],
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = buildCreateUserPayload(form, { department, isSuperAdmin });
    if (payload) {
      onSubmit(payload);
    } else {
      toast.error('Please fill in all required fields: Username, Full Name, Password (min 6 chars), and Department.');
    }
  };

  // A department administrator cannot mint peers or superiors — the server
  // refuses administrator-tier roles from a non-owner — so they are not offered.
  const roleOptions = rolesForDepartment(form.department).filter(
    (option) => isSuperAdmin || (option.value !== 'admin' && option.value !== 'department_admin'),
  );

  // Designations for the selected department, from master data — filtered to
  // only those with a baseRole set. A designation missing baseRole can't
  // safely grant an RBAC role, so it's excluded here rather than silently
  // defaulting to "employee" (2026-08-16 — that silent default was the whole
  // bug: role and designation used to be the same form field).
  const departmentDesignations = masterDesignations
    .filter((d) => !form.department || !d.department || d.department.toLowerCase() === form.department.toLowerCase())
    .filter((d) => d.active !== false);
  const usableDesignations = departmentDesignations.filter((d) => !!d.baseRole);
  const missingBaseRoleCount = departmentDesignations.length - usableDesignations.length;

  // Master designation data governs Designation selection only once the
  // selected department actually has at least one USABLE designation;
  // otherwise this falls back to the plain role list, same behavior as
  // before designation master data existed for that department.
  const hasUsableDesignations = usableDesignations.length > 0;

  const designationOptions = hasUsableDesignations
    ? usableDesignations.map((d) => ({ value: d.value, label: d.label }))
    : roleOptions.map(({ value, label }) => ({ value, label }));

  const selectDesignation = (value: string) => {
    setSelectedDesignationValue(value);
    if (hasUsableDesignations) {
      const chosen = usableDesignations.find((d) => d.value === value);
      const nextRole = (chosen?.baseRole as CreateUserRole) || 'employee';
      setForm((prev) => ({
        ...prev,
        designation: chosen?.label ?? '',
        role: nextRole,
        workspaceAccess: defaultWorkspaceAccessForRole(prev.department, nextRole),
      }));
    } else {
      // Fallback mode: the dropdown IS the role list (no usable designation
      // master data for this department yet), so designation just mirrors
      // the role's label for display/storage purposes.
      const chosen = roleOptions.find((r) => r.value === value);
      setForm((prev) => ({
        ...prev,
        designation: chosen?.label ?? '',
        role: value as CreateUserRole,
        workspaceAccess: defaultWorkspaceAccessForRole(prev.department, value as CreateUserRole),
      }));
    }
  };

  const canSubmit = Boolean(
    form.username.trim()
    && form.displayName.trim()
    && form.password.length >= 6
    && (!isSuperAdmin || (form.department && selectedDesignationValue)),
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create User"
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button type="submit" form="create-user-form" loading={loading} disabled={!canSubmit || loading}>
            Create User
          </Button>
        </>
      }
    >
      <form id="create-user-form" onSubmit={handleSubmit} className="space-y-5" autoComplete="off">
        <section className="space-y-3">
          <div>
            <h3 className="text-data-md font-semibold text-on-surface">User details</h3>
            <p className="text-body-sm text-on-surface-variant">Login identity and the temporary first-time password.</p>
          </div>
          <Input
            label="Username *"
            name="new-user-username"
            value={form.username}
            onChange={(e) => set('username', e.target.value)}
            autoComplete="off"
            placeholder="firstname or firstname@company.com"
            hint="2â€“30 characters; letters, numbers, dots, dashes and the company email suffix"
            required
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              label="Display Name *"
              name="new-user-displayname"
              value={form.displayName}
              onChange={(e) => set('displayName', e.target.value)}
              autoComplete="name"
              placeholder="Full name"
              required
            />
            <Input
              label="Temporary Password *"
              name="new-user-password"
              type="password"
              autoComplete="new-password"
              value={form.password}
              onChange={(e) => set('password', e.target.value)}
              placeholder="At least 6 characters"
              required
            />
          </div>
        </section>

        <section className="rounded-card border border-outline bg-surface-variant/20 p-4">
          <div className="mb-4 flex items-start gap-3">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-data-md font-semibold text-on-primary">1</span>
            <div>
              <h3 className="text-data-md font-semibold text-on-surface">Department and designation</h3>
              <p className="text-body-sm text-on-surface-variant">
                {isSuperAdmin
                  ? 'Select the department first. Available designations will update automatically.'
                  : 'Department administrators can add employees only to their own team.'}
              </p>
            </div>
          </div>

          {isSuperAdmin ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <Select
                label="Department *"
                name="new-user-department"
                value={form.department}
                onChange={(e) => setDepartment(e.target.value)}
                placeholder="Select department"
                options={masterDepartments.length > 0
                  ? masterDepartments.map((d) => ({ value: d.value, label: d.label }))
                  : CREATE_DEPTS.map((value) => ({ value, label: DEPT_LABELS[value] ?? value }))
                }
                required
              />
              <Select
                label="Designation *"
                name="new-user-designation"
                placeholder={form.department ? 'Select designation' : undefined}
                value={selectedDesignationValue}
                onChange={(e) => selectDesignation(e.target.value)}
                options={designationOptions}
                disabled={!form.department}
                required
              />
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              <Input label="Department" name="new-user-department-locked" value={DEPT_LABELS[department] ?? department} readOnly disabled />
              <Input label="Designation" name="new-user-role-locked" value="Employee" readOnly disabled />
            </div>
          )}

          {isSuperAdmin && form.department && missingBaseRoleCount > 0 && (
            <p className="mt-2 text-body-sm text-warning-text">
              {missingBaseRoleCount} designation{missingBaseRoleCount === 1 ? '' : 's'} in this department {missingBaseRoleCount === 1 ? "isn't" : "aren't"} shown here yet — set a Base Role for {missingBaseRoleCount === 1 ? 'it' : 'them'} on the Designations master data page first.
            </p>
          )}

          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <Select
              label="Shift"
              name="new-user-shift"
              value={form.shiftType}
              onChange={(e) => set('shiftType', e.target.value as CreateUserFormState['shiftType'])}
              options={masterShifts.length > 0
                ? masterShifts.map((s) => ({ value: s.name || s._id, label: s.name }))
                : [
                    { value: 'Domestic', label: 'Domestic' },
                    { value: 'International', label: 'International' },
                  ]
              }
            />
            {isSuperAdmin && form.department && form.designation && (
              <div className="rounded border border-outline bg-surface px-3 py-2">
                <p className="text-label-caps uppercase text-on-surface-variant">Selected designation</p>
                <p className="mt-1 text-data-md font-medium text-on-surface">{form.designation}</p>
                {hasUsableDesignations && (
                  <p className="mt-0.5 text-[11px] text-on-surface-variant">
                    Grants role: {roleOptions.find((r) => r.value === form.role)?.label || form.role}
                  </p>
                )}
              </div>
            )}
          </div>
        </section>

        {form.department && (
          <fieldset className="rounded-card border border-outline p-4">
            <legend className="px-1 text-data-md font-semibold text-on-surface">Module access</legend>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <p className="text-body-sm text-on-surface-variant">
                Everything here is your call — check or uncheck anything. Boxes marked
                <span className="mx-1 inline-flex items-center gap-1 rounded-full bg-primary-container px-2 py-0.5 text-[11px] font-semibold text-on-primary-container">
                  <Sparkles className="h-3 w-3" />suggested
                </span>
                start ticked for this department and role, but nothing is locked.
              </p>
              <span className="rounded-full bg-primary-container px-2.5 py-1 text-label-caps font-semibold uppercase text-on-primary-container">
                {form.workspaceAccess.length} modules enabled
              </span>
            </div>

            <Input
              leftIcon={<Search className="h-4 w-4" />}
              placeholder="Find a module… (e.g. Dispatch)"
              value={moduleSearch}
              onChange={(e) => setModuleSearch(e.target.value)}
              className="mb-3"
            />

            <div className="max-h-80 space-y-4 overflow-y-auto pr-1">
              {groupedModules.length === 0 && (
                <p className="py-4 text-center text-body-sm text-on-surface-variant">No module matches "{moduleSearch}".</p>
              )}
              {groupedModules.map(([group, modules]) => (
                <div key={group}>
                  <h4 className="mb-2 text-label-caps font-semibold uppercase tracking-wide text-on-surface-variant">
                    {MODULE_GROUP_LABELS[group] ?? group}
                  </h4>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {modules.map((module) => {
                      const checked = form.workspaceAccess.includes(module.key);
                      const suggested = suggestedWorkspaceAccess.includes(module.key);
                      return (
                        <label
                          key={module.key}
                          className={`flex cursor-pointer gap-3 rounded border p-3 transition-colors ${
                            checked
                              ? 'border-primary bg-primary-container/30'
                              : 'border-outline bg-surface hover:bg-surface-variant'
                          }`}
                        >
                          <input type="checkbox" className="sr-only" checked={checked} onChange={() => toggleWorkspaceAccess(module.key)} />
                          <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                            checked ? 'border-primary bg-primary text-on-primary' : 'border-outline bg-surface'
                          }`} aria-hidden>
                            {checked && <Check className="h-3.5 w-3.5" />}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center gap-1.5">
                              <span className="block text-data-md font-medium text-on-surface">{module.label}</span>
                              {suggested && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-primary-container px-1.5 py-0.5 text-[10px] font-semibold uppercase text-on-primary-container">
                                  <Sparkles className="h-2.5 w-2.5" />suggested
                                </span>
                              )}
                            </span>
                            <span className="block text-body-sm text-on-surface-variant">{module.description}</span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </fieldset>
        )}

        <p className="text-body-sm text-on-surface-variant">
          {isSuperAdmin
            ? 'The new user receives exactly the modules checked above — nothing more, nothing less.'
            : 'You can grant any module you hold yourself. Anything beyond your own access is refused, not quietly dropped.'}
        </p>
      </form>
    </Modal>
  );
}
